import { useContext, useMemo, useState } from "react";
import { AppContext } from "../App";
import { IoArrowForwardCircleOutline, IoChevronBack, IoChevronForward } from "react-icons/io5";

export default function ServiceDetailPage({
  label = "Service",
  title,
  subtitle,
  overview = "",
  images = [],
  highlights = [],
  metrics = [],
  packages = [],
  activities = [],
  packageHeading = "Featured Packages",
  activityHeading = "Experiences",
  ctaLabel = "Plan This Trip",
}) {
  const appContext = useContext(AppContext);
  const [slideIndex, setSlideIndex] = useState(0);

  const media = useMemo(() => {
    if (images.length > 0) return images;
    return [];
  }, [images]);

  function goPrev() {
    if (media.length < 2) return;
    setSlideIndex((prev) => (prev - 1 + media.length) % media.length);
  }

  function goNext() {
    if (media.length < 2) return;
    setSlideIndex((prev) => (prev + 1) % media.length);
  }

  const paragraphs = overview
    .split(". ")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (item.endsWith(".") ? item : `${item}.`));

  function openContact() {
    appContext.navTo({ item: "contact_us" });
  }

  function handlePackageAction(pkg) {
    if (pkg?.onClick) {
      pkg.onClick();
      return;
    }

    if (pkg?.nav) {
      appContext.navTo(pkg.nav);
      return;
    }

    openContact();
  }

  return (
    <section className="MainBodySection" style={{ background: "linear-gradient(180deg, #f7fbff 0%, #eef5fb 100%)" }}>
      <div className="container">
        <div
          className="mx-auto"
          style={{
            maxWidth: 1040,
            borderRadius: 28,
            padding: "26px 20px 22px",
            background: "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(135deg, rgba(15,47,79,.24), rgba(187,122,36,.3)) border-box",
            border: "1px solid transparent",
            boxShadow: "0 24px 56px rgba(8,24,42,.13)",
          }}
          data-aos="fade-up"
        >
          <div className="text-center mx-auto" style={{ maxWidth: 860 }}>
            

            <h1 className="font-bold mb-2" style={{ color: "#132335", fontSize: "clamp(30px, 4.6vw, 50px)", lineHeight: 1.15 }}>
              {title}
            </h1>

            <p className="mb-4" style={{ color: "#4f5f72", lineHeight: 1.75, fontSize: "clamp(15px, 1.55vw, 19px)" }}>
              {subtitle}
            </p>

            {metrics.length > 0 && (
              <div className="row g-2 justify-content-center mt-3 mb-5">
                {metrics.map((metric) => (
                  <div key={`${metric.value}-${metric.label}`} className="col-6 col-md-4">
                    <article
                      className="svc-page-metric"
                      style={{
                        padding: "12px 10px",
                        textAlign: "center",
                        height: "100%",
                      }}
                    >
                      <p className="mb-1 font-semi-bold" style={{ color: "#102c49", fontSize: "clamp(18px, 2vw, 24px)", lineHeight: 1.2 }}>
                        {metric.value}
                      </p>
                      <p className="mb-0" style={{ color: "#5b6a7d", fontSize: 13, lineHeight: 1.45 }}>
                        {metric.label}
                      </p>
                    </article>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              position: "relative",
              minHeight: 430,
              borderRadius: 24,
              overflow: "hidden",
              border: "1px solid rgba(15,47,79,.15)",
              boxShadow: "0 18px 36px rgba(8,24,42,.14)",
            }}
            data-aos="zoom-in"
          >
            {media.map((item, index) => (
              <article
                key={item.src + index}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: index === slideIndex ? 1 : 0,
                  transition: "opacity .6s ease",
                }}
              >
                <img
                  src={item.src}
                  alt={item.alt || title}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(180deg, rgba(6,21,32,.1), rgba(6,21,32,.82))",
                  }}
                />
                <div style={{ position: "absolute", left: 22, right: 22, bottom: 20, color: "#fff", textAlign: "center" }}>
                  <h3 className="font-semi-bold mb-1" style={{ fontSize: "clamp(22px, 2.4vw, 30px)" }}>
                    {item.captionTitle || title}
                  </h3>
                  <p className="mb-0" style={{ lineHeight: 1.65, color: "#d9e5f1" }}>
                    {item.captionText || subtitle}
                  </p>
                </div>
              </article>
            ))}

            {media.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous slide"
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 62,
                    width: 40,
                    height: 40,
                    border: "none",
                    borderRadius: 12,
                    background: "rgba(255,255,255,.92)",
                  }}
                >
                  <IoChevronBack size={20} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next slide"
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    width: 40,
                    height: 40,
                    border: "none",
                    borderRadius: 12,
                    background: "rgba(255,255,255,.92)",
                  }}
                >
                  <IoChevronForward size={20} />
                </button>
              </>
            )}
          </div>

          <div className="mx-auto mt-4" style={{ maxWidth: 900 }} data-aos="fade-up">
            <h2 className="font-semi-bold mb-3 text-center" style={{ color: "#142436", fontSize: "clamp(24px, 2.4vw, 30px)" }}>
              Trip Details
            </h2>

            <div className="d-grid" style={{ gap: 10 }}>
              {paragraphs.length > 0 ? (
                paragraphs.map((item, index) => (
                  <p
                    key={index}
                    className="mb-0"
                    style={{
                      color: "#4f5f72",
                      lineHeight: 1.75,
                      textAlign: "center",
                    }}
                  >
                    {item}
                  </p>
                ))
              ) : (
                <p className="mb-0" style={{ color: "#4f5f72", lineHeight: 1.75, textAlign: "center" }}>
                  Full itinerary details will be tailored to your travel dates and preferences.
                </p>
              )}
            </div>
          </div>

          {highlights.length > 0 && (
            <div className="row g-3 mt-3 justify-content-center" data-aos="fade-up">
              {highlights.map((item) => (
                <div key={item} className="col-md-6">
                  <article
                    style={{
                      height: "100%",
                      borderRadius: 16,
                      border: "1px solid rgba(15,47,79,.14)",
                      background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                      padding: "14px 14px",
                      boxShadow: "0 10px 22px rgba(9,25,43,.08)",
                    }}
                  >
                    <p className="mb-0" style={{ color: "#4f5f72", lineHeight: 1.65, textAlign: "center" }}>
                      {item}
                    </p>
                  </article>
                </div>
              ))}
            </div>
          )}

          {packages.length > 0 && (
            <div className="my-5" data-aos="fade-up">
              <h2 className="font-semi-bold mb-3 text-center" style={{ color: "#142436", fontSize: "clamp(22px, 2.2vw, 28px)" }}>
                {packageHeading}
              </h2>
              
            </div>
          )}

          {activities.length > 0 && (
            <div className="mt-4" data-aos="fade-up">
              <h2 className="font-semi-bold mb-3 text-center" style={{ color: "#142436", fontSize: "clamp(22px, 2.2vw, 28px)" }}>
                {activityHeading}
              </h2>
              <div className="row g-3">
                {activities.map((activity) => (
                  <div key={activity.title} className="col-md-6">
                    <article
                      style={{
                        height: "100%",
                        borderRadius: 16,
                        border: "1px solid rgba(15,47,79,.15)",
                        background: "#ffffff",
                        padding: "14px 14px",
                        boxShadow: "0 10px 22px rgba(9,25,43,.08)",
                      }}
                    >
                      <h3 className="font-semi-bold mb-2" style={{ color: "#142436", fontSize: 21 }}>
                        {activity.title}
                      </h3>
                      <p className="mb-0" style={{ color: "#5a697b", lineHeight: 1.65 }}>
                        {activity.description}
                      </p>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-5">
            <button
              onClick={()=>appContext.navTo({item:'plan-trip'})}
              style={{
                border: "none",
                borderRadius: 999,
                background: "#102030",
                color: "#fff",
                padding: "11px 18px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 600,
              }}
            >
              {ctaLabel} <IoArrowForwardCircleOutline size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
